import React, { Component } from "react";
import PropTypes from "prop-types";
import { Field, Fields, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { Col } from "react-bootstrap";
import { debounce } from "lodash";
import * as actions from "MODULES/parts/actions";
import {
  form,
  Form as AppForm,
  Check,
  CurrencyField,
  FormInput,
  FormRow,
  NSNField,
  Select,
  CageField,
  UNCodeField,
  CheckGroup,
  ECCNField
} from "MODULES/shared/components/Forms";
import {
  AUTO_ADJUST_SRP_EXCLUSION,
  CLASSIFIED_MEMO,
  DESCRIPTION,
  PART_NUMBER,
  SPECIAL_CONTAINER_FEE,
  WARRANTY_OVERRIDE,
  HTSUS_CODE,
  SCHEDULE_B_OR_HT_CODE,
  IMPORTANT_MEMO,
  SPECIAL_CONTAINER_REQ,
  PURCHASED_AT,
  MLP,
  COUNTRY_OF_MFG,
  DOM_LIFE_YEARS,
  ESD,
  NSN,
  UNITED_NATIONS_CODE,
  EUROPEAN_COMMERCE_CONTROL_NUMBER,
  CAGE,
  MANUFACTURER,
  DOM_SENSITIVE
} from "MODULES/parts/constants";
import { DashedSeparator } from "MODULES/shared/components";
import countries from "LIB/countries";
import WeightAndDimensionsSection from "MODULES/parts/components/shared/FormSections/WeightAndDimensionsSection";
import { ADMIN, ROLE, USER } from "MODULES/users/constants";
import { properCase } from "LIB/formatters/text";
import { arrayToOptions } from "LIB/forms";
import { WARRANTY_OPTIONS } from "MODULES/inventories/constants";
import PartNumber from "../shared/PartNumber";
import Description from "../shared/Description";
import Warnings from "./Warnings";
import validate from "./validate";

const debounceTimeout = 350;
const FORM_NAME = "createPart";

export class Form extends Component {
  state = {
    checkingForDuplicates: false,
    hasDuplicates: false
  };

  notify = () =>
    this.setState({ checkingForDuplicates: true, hasDuplicates: false });

  check = debounce(partNumber => {
    this.props.search(partNumber).then(results => {
      this.setState({ checkingForDuplicates: false });
      this.setState({ hasDuplicates: results.length > 0 });
    });
  }, debounceTimeout);

  resetScFee = scReq => {
    if (scReq === false) {
      this.props.change(SPECIAL_CONTAINER_FEE, null);
    }
  };

  render() {
    const { scReq, submitting, role, mlp, purchasedAt, dom } = this.props;
    const countryOptions = countries.map(c => ({
      value: c.code,
      text: c.name
    }));

    return (
      <AppForm
        {...this.props}
        submitting={submitting || this.state.checkingForDuplicates}
      >
        <AppForm.Body>
          {this.state.hasDuplicates && (
            <div className="alert alert-warning">
              <strong>Heads up!</strong> A base part number matching this part
              number already exists!
            </div>
          )}
          <FormRow>
            <Col md={4}>
              <Fields
                component={PartNumber}
                names={[PART_NUMBER, DESCRIPTION]}
                notify={this.notify}
                check={this.check}
              />
            </Col>
            <Col md={4}>
              <Fields
                component={Description}
                names={[PART_NUMBER, DESCRIPTION]}
              />
            </Col>
            <Col md={4}>
              <Field name={MANUFACTURER} component={FormInput} label="MFG" />
            </Col>
          </FormRow>
          <hr className="hr-sm margin-top-none" />
          <FormRow>
            <Col md={2}>
              <CageField
                name={CAGE}
                component={FormInput}
                label={properCase(CAGE)}
              />
            </Col>
            <Col md={2}>
              <ECCNField
                name={EUROPEAN_COMMERCE_CONTROL_NUMBER}
                component={FormInput}
                label={"ECCN"}
              />
            </Col>
            <Col md={3}>
              <UNCodeField
                name={UNITED_NATIONS_CODE}
                component={FormInput}
                label="UN Code"
              />
            </Col>
            <Col md={5}>
              <NSNField name={NSN} component={FormInput} label="NSN" />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={3}>
              <CurrencyField
                name={MLP}
                label="Manufacturer List Price"
                required={purchasedAt}
              />
            </Col>
            <Col md={3}>
              <Field
                name={PURCHASED_AT}
                component={FormInput}
                label="Date of Price"
                type="date"
                required={mlp}
              />
            </Col>
            <Col md={5}>
              <Field
                component={Select}
                options={countryOptions}
                name={COUNTRY_OF_MFG}
                label="Ctry of Mfg"
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={3}>
              <CheckGroup justified>
                <Field
                  component={Check}
                  name={DOM_SENSITIVE}
                  label={"DOM Sensitive"}
                  icon="fa-calendar"
                />
              </CheckGroup>
            </Col>
            <Col md={3}>
              {dom && (
                <div className="form-group">
                  <Field
                    name={DOM_LIFE_YEARS}
                    component={FormInput}
                    label="DOM Life Years"
                  />
                </div>
              )}
            </Col>
          </FormRow>
          <WeightAndDimensionsSection />
          <DashedSeparator />
          <Warnings formName={FORM_NAME} />
          <FormRow>
            <Col md={6}>
              <div className="form-group">
                <label htmlFor="special-container" className="control-label">
                  Special Container
                </label>
                <br />
                <div
                  id="special-container"
                  className="btn-group btn-group-justified"
                  data-toggle="buttons"
                >
                  <Field
                    onChange={(ev, val) => this.resetScFee(val)}
                    component={Check}
                    name={SPECIAL_CONTAINER_REQ}
                    label="Special Container Required"
                    icon="fa-cube"
                  />
                </div>
              </div>
            </Col>
            <Col md={3}>
              {scReq && (
                <CurrencyField
                  name={SPECIAL_CONTAINER_FEE}
                  label={properCase(SPECIAL_CONTAINER_FEE)}
                  required
                />
              )}
            </Col>
            <Col md={3}>
              <div className="form-group">
                <label htmlFor="esd" className="control-label">
                  ESD
                </label>
                <br />
                <div
                  className="btn-group btn-group-justified"
                  data-toggle="buttons"
                >
                  <Field
                    component={Check}
                    name={ESD}
                    label={ESD.toUpperCase()}
                  />
                </div>
              </div>
            </Col>
          </FormRow>
          <DashedSeparator />
          <FormRow>
            <Col md={6}>
              <Field
                component={Select}
                options={arrayToOptions(WARRANTY_OPTIONS)}
                name={WARRANTY_OVERRIDE}
                label={properCase(WARRANTY_OVERRIDE)}
                disabled={role !== ADMIN}
              />
            </Col>
            <Col md={6}>
              <CheckGroup justified>
                <Field
                  component={Check}
                  name={AUTO_ADJUST_SRP_EXCLUSION}
                  label={properCase(AUTO_ADJUST_SRP_EXCLUSION)}
                />
              </CheckGroup>
            </Col>
          </FormRow>
          <DashedSeparator />
          <FormRow>
            <Col md={12}>
              <Field
                name={IMPORTANT_MEMO}
                component={FormInput}
                label={properCase(IMPORTANT_MEMO)}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col md={12}>
              <Field
                name={CLASSIFIED_MEMO}
                component={FormInput}
                label={properCase(CLASSIFIED_MEMO)}
              />
            </Col>
          </FormRow>
          <DashedSeparator />
          <FormRow>
            <Col md={6} xs={12}>
              <Field
                name={SCHEDULE_B_OR_HT_CODE}
                component={FormInput}
                label={properCase(SCHEDULE_B_OR_HT_CODE)}
              />
            </Col>
            <Col md={6} xs={12}>
              <Field
                name={HTSUS_CODE}
                component={FormInput}
                label={properCase(HTSUS_CODE).toUpperCase()}
              />
            </Col>
          </FormRow>
        </AppForm.Body>
        {this.props.children}
      </AppForm>
    );
  }
}

Form.propTypes = {
  submitting: PropTypes.bool.isRequired,
  role: PropTypes.string.isRequired,
  search: PropTypes.func.isRequired,
  mlp: PropTypes.number,
  scReq: PropTypes.bool,
  dom: PropTypes.bool,
  purchasedAt: PropTypes.string
};
const selector = formValueSelector(FORM_NAME);

export default connect(
  state => ({
    mlp: selector(state, MLP),
    role: state.session.getIn([USER, ROLE]),
    scReq: selector(state, SPECIAL_CONTAINER_REQ),
    dom: selector(state, "dom_sensitive"),
    purchasedAt: selector(state, PURCHASED_AT)
  }),
  (dispatch, ownProps) => ({
    onSubmit: data => {
      data.tier_type = ownProps.tierType;
      return dispatch(actions.create(data));
    },
    search: partNumber =>
      dispatch(actions.searchMatchingPartNumbers(partNumber))
  })
)(
  form({
    form: FORM_NAME,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/parts/components/CreatePart/Form.jsx